import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import {
  DEFAULT_LANDING_URL,
  returnUrlExtras,
  safeReturnUrl,
} from '@core/helpers/return-url.helper';

/**
 * Where the interrupted destination is parked while the person logs back in.
 *
 * `sessionStorage` rather than `localStorage`: {@link AuthService.clearSession} wipes localStorage
 * on the way out, and the login happens in the tab that was interrupted — another tab has its own
 * place to be, and should not inherit this one's.
 */
const RETURN_URL_STASH_KEY = 'sbe.session.returnUrl';

/**
 * Owns what happens when a session ends, and where the person lands once they are back in.
 *
 * There are two kinds of ending and they behave differently on purpose:
 *
 * - **Involuntary** ({@link sessionEnded}) — the refresh token expired, a 401 could not be
 *   recovered, the server hung up. Nobody asked for this, so where they were is remembered and
 *   logging in again puts them right back there.
 * - **Deliberate** ({@link signedOut}) — they clicked Log out. Returning them to the page they
 *   just chose to leave would be wrong, so the remembered destination is dropped.
 *
 * Everything that ends a session goes through here, so that distinction is made in one file rather
 * than re-decided at each call site.
 */
@Injectable({ providedIn: 'root' })
export class SessionRedirectService {
  private readonly router = inject(Router);

  /**
   * A session that ended without being asked to. Remembers the current page, then sends the person
   * to log in again.
   *
   * Returns nothing rather than the navigation: the callers are interceptors reacting to a failed
   * refresh, mid-stream in an RxJS pipe with nothing to await it. A cancelled navigation is
   * swallowed here — the stash still holds the destination, which is what the login page falls
   * back to, so there is nothing for a caller to recover.
   *
   * @param fromUrl Where they were. Defaults to the page currently on screen, which is what an
   *   interceptor reacting to a failed refresh wants.
   */
  sessionEnded(fromUrl: string = this.router.url): void {
    this.rememberDestination(fromUrl);
    this.router.navigate(['/login'], returnUrlExtras(fromUrl)).catch(() => {
      // Cancelled by a competing redirect; see above.
    });
  }

  /** A deliberate sign-out: forget where they were, then send them to the login page. */
  async signedOut(): Promise<void> {
    this.forgetDestination();
    await this.router.navigate(['/login']);
  }

  /**
   * Remembers where someone was heading when they were turned away.
   *
   * Guards call this directly rather than going through {@link sessionEnded}: they answer with a
   * `UrlTree` instead of navigating, so the redirect is not theirs to perform.
   */
  rememberDestination(url: string): void {
    const destination = safeReturnUrl(url);
    if (destination === null) return;
    try {
      sessionStorage.setItem(RETURN_URL_STASH_KEY, destination);
    } catch {
      // Blocked in some privacy modes. The `?returnUrl=` on the login URL still carries it.
    }
  }

  /**
   * Where to land now that the person is back in — and forgets it in the same breath: one
   * interrupted journey, one return trip.
   *
   * The address bar wins, being the freshest thing we have and what a guard has just written. The
   * stash is the fallback for when that param did not survive — a detour through Forgot Password,
   * say, or a redirect that dropped the query string on the way.
   */
  consumeLandingUrl(fromQueryParam: string | null): string {
    const stashed = this.readDestination();
    this.forgetDestination();
    return safeReturnUrl(fromQueryParam) ?? stashed ?? DEFAULT_LANDING_URL;
  }

  /** Drops the remembered destination without navigating anywhere. */
  forgetDestination(): void {
    try {
      sessionStorage.removeItem(RETURN_URL_STASH_KEY);
    } catch {
      // As above — if the store is unreachable then nothing was ever put in it.
    }
  }

  /** The remembered destination, if there is one still worth honouring. */
  private readDestination(): string | null {
    try {
      return safeReturnUrl(sessionStorage.getItem(RETURN_URL_STASH_KEY));
    } catch {
      return null;
    }
  }
}
